import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import { cardsAPI } from '../services/api';

const REFRESH_SECONDS = 15;

export default function CardViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [countdown, setCountdown] = useState(REFRESH_SECONDS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const fetchCard = async () => {
    if (!id) return;
    try {
      const c = await cardsAPI.getCard(id);
      setCard(c);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load card');
    } finally {
      setLoading(false);
    }
  };

  const fetchQR = async () => {
    if (!id) return;
    try {
      const d = await cardsAPI.getQR(id);
      setQrCode(d.qrCode);
      setCountdown(d.expiresIn ?? REFRESH_SECONDS);
    } catch (e) {
      console.warn('QR fetch failed', e);
    }
  };

  useEffect(() => {
    fetchCard();
  }, [id]);

  useEffect(() => {
    if (!id || loading) return;
    fetchQR();
  }, [id, loading]);

  useEffect(() => {
    if (countdown <= 0) {
      fetchQR();
      return;
    }
    timerRef.current = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [countdown, id]);

  if (loading) return <Container sx={{ py: 3 }}><CircularProgress /></Container>;
  if (error || !card) return <Container sx={{ py: 3 }}><Typography color="error">{error || 'Card not found'}</Typography><Button onClick={() => navigate('/cards')}>Back</Button></Container>;

  const data = card.card_data && typeof card.card_data === 'object' ? card.card_data : {};
  const memberName = data.memberName || [data.firstName, data.lastName].filter(Boolean).join(' ') || '—';

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Digital Card</Typography>
      {card.card_front_url ? (
        <Box component="img" src={card.card_front_url} alt="Card front" sx={{ width: '100%', maxWidth: 360, borderRadius: 2, mb: 2 }} />
      ) : (
        <Paper sx={{ p: 3, mb: 2, textAlign: 'center' }}>
          <Typography variant="body1">{card.insurer_name}</Typography>
          <Typography variant="body2">{memberName}</Typography>
          <Typography variant="body2">{card.policy_number}</Typography>
        </Paper>
      )}
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="subtitle2" gutterBottom>Scan at clinic or hospital</Typography>
        {qrCode ? (
          <>
            <Box component="img" src={qrCode} alt="QR" sx={{ width: 200, height: 200 }} />
            <Typography variant="body2" color="textSecondary">Refreshes in {countdown} seconds</Typography>
          </>
        ) : (
          <CircularProgress size={40} />
        )}
      </Paper>
      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button variant="outlined" onClick={() => cardsAPI.downloadCard(id)}>Download PDF</Button>
        <Button onClick={() => navigate('/cards')}>Back to Cards</Button>
      </Box>
    </Container>
  );
}
